import React from 'react';
import { Box, Button, Flex, FlexItem, Text } from '@bigcommerce/big-design';
import { MetricCard } from '../common/MetricCard';
import type { PendingNotificationsResponse } from '@find-my-part/types';
import type { PendingWorkSnapshot } from '../../hooks/usePendingWork';
import type { AdminLocation } from '../Shell/location';

type CountKey = 'pendingQuestions' | 'questionsWithPendingReplies' | 'pendingMedia';

const TILES: ReadonlyArray<{
  key: CountKey;
  title: string;
  unit: string;
  destination: AdminLocation;
}> = [
  {
    key: 'pendingQuestions',
    title: 'Questions awaiting review',
    unit: 'questions',
    destination: { section: 'questions', view: 'pending' },
  },
  {
    key: 'questionsWithPendingReplies',
    title: 'Questions with replies to review',
    unit: 'distinct questions',
    destination: { section: 'questions', view: 'needsReview' },
  },
  {
    key: 'pendingMedia',
    title: 'Media awaiting review',
    unit: 'media items',
    destination: { section: 'media', view: 'pendingReview' },
  },
];

function formatAge(seconds: number | null, count: number): string {
  if (count === 0) return 'No pending items';
  if (seconds === null) return 'Oldest item age unavailable';
  if (seconds < 60) return `Oldest item age: ${seconds}s`;
  if (seconds < 3600) return `Oldest item age: ${Math.floor(seconds / 60)}m`;
  return `Oldest item age: ${Math.floor(seconds / 3600)}h`;
}

function countFor(data: PendingNotificationsResponse | null, key: CountKey): number | null {
  return data ? data[key] : null;
}

export interface OverviewPanelProps {
  pending: PendingWorkSnapshot;
  navigate: (location: AdminLocation) => void;
}

export function OverviewPanel({
  pending,
  navigate,
}: OverviewPanelProps): React.ReactElement {

  return (
    <Box padding="medium" data-testid="pending-work-overview">
      <Flex justifyContent="space-between" alignItems="center" marginBottom="medium">
        <FlexItem>
          <Text color="secondary" marginBottom="none">
            Actionable moderation work for this store. Counts are queue-scoped and use the same
            predicates as their destinations.
          </Text>
        </FlexItem>
        <FlexItem>
          <Button
            variant="subtle"
            onClick={() => void pending.refresh()}
            isLoading={pending.refreshing}
            data-testid="overview-refresh"
          >
            {pending.freshness === 'fresh' ? 'Refresh' : 'Retry'}
          </Button>
        </FlexItem>
      </Flex>

      <Flex flexGap="1rem" flexWrap="wrap" marginBottom="medium">
        {TILES.map((tile) => {
          const count = countFor(pending.data, tile.key);
          const unavailable = pending.freshness === 'unavailable' || count === null;
          const stale = pending.freshness === 'stale';
          const age = pending.data?.oldestItemAgeSeconds[tile.key] ?? null;
          return (
            <FlexItem key={tile.key} style={{ minWidth: '220px', flex: '1 1 220px' }}>
              <MetricCard
                title={tile.title}
                testId={`overview-tile-${tile.key}`}
                value={unavailable ? '—' : count}
                unit={tile.unit}
                chip={{
                  variant: unavailable ? 'secondary' : stale ? 'warning' : 'success',
                  label: unavailable ? 'Unavailable' : stale ? 'Stale' : 'Fresh',
                }}
                ageLine={!unavailable ? formatAge(age, count ?? 0) : null}
                asOfLine={pending.data ? `As of ${new Date(pending.data.asOf).toLocaleString()}` : null}
                action={
                  <Button
                    variant="subtle"
                    aria-label={`Open ${tile.key === 'pendingQuestions' ? 'pending questions queue' : tile.key === 'questionsWithPendingReplies' ? 'questions with pending replies queue' : 'pending media review queue'}`}
                    onClick={() => navigate(tile.destination)}
                    data-testid={`overview-open-${tile.key}`}
                  >
                    Open filtered queue
                  </Button>
                }
              />
            </FlexItem>
          );
        })}
      </Flex>

    </Box>
  );
}
